import type { BlockHolder } from '@/api/masters';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { formatDate, formatDateTime } from '@core/format/dates';
import { holdCountdown } from '@/format/holds';

/**
 * Who is holding a garment, and how to reach them.
 *
 * <p>
 * Shared between the availability search and the booking counter because both screens answer the
 * same question at the same moment — a customer is standing there wanting a garment somebody else
 * has spoken for — and a shop that reads "Committed" on one screen and "On hold" on the other stops
 * trusting either.
 * </p>
 *
 * <p>
 * <strong>The number is a `tel:` link, not text.</strong> Chasing the first party is the entire
 * point of showing this: on the tablet at the counter it dials, and on a desk it is still the thing
 * that gets copied. Text would leave a clerk typing ten digits off a screen while a customer waits.
 * </p>
 */
export function HeldByLine({
  holder,
  tentative,
  className,
}: {
  holder: BlockHolder;
  tentative: boolean;
  className?: string;
}) {
  const countdown = holdCountdown(holder.holdExpiresOn);

  return (
    <span
      // A Tailwind length literal consumes neither antd token nor `--scale-*`, so `text-[12px]`
      // here silently ignored the person's text size on all three screens that render this line.
      className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[length:var(--scale-list-meta)] ${
        className ?? ''
      }`}
    >
      <span className="font-medium text-gray-900">{holder.customerName || 'Unnamed customer'}</span>

      {holder.customerMobile && (
        <a
          href={`tel:${holder.customerMobile}`}
          className="font-mono-nums text-primary hover:underline"
          // The row this sits in is usually itself clickable — on the booking counter the whole
          // tile picks the garment — and dialling must not also pick it.
          onClick={(event) => event.stopPropagation()}
        >
          {holder.customerMobile}
        </a>
      )}

      {holder.documentNumber ? (
        <span className="font-mono-nums text-default">{holder.documentNumber}</span>
      ) : (
        // A hold takes no number. Saying so is better than a blank space that reads as missing data.
        tentative && <span className="text-default">not yet numbered</span>
      )}

      {holder.eventDate && <span className="text-default">for {formatDate(holder.eventDate)}</span>}

      {tentative && holder.holdExpiresOn && (
        <span className="text-warning">
          until {formatDateTime(holder.holdExpiresOn)}
          {countdown ? ` · ${countdown}` : ''}
        </span>
      )}
    </span>
  );
}

/**
 * The badge over that line.
 *
 * <p>
 * Two words carry the whole distinction: a garment somebody <em>holds</em> is one phone call from
 * being free, and one somebody has <em>booked</em> is not. Reading the block type instead — which is
 * what every screen did before — labels both "Booking", and a shop turns a paying customer away for
 * a claim that was about to lapse anyway.
 * </p>
 */
export function HeldBadge({ tentative }: { tentative: boolean }) {
  const tone: StatusTone = tentative ? 'warning' : 'brand';

  return <StatusBadge tone={tone}>{tentative ? 'On hold' : 'Booked'}</StatusBadge>;
}
